// src/lib/analytics.ts
'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import type { EventParams } from '@/types/gtag';

export function useGoogleAnalytics(): void {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!measurementId || measurementId === 'your-google-analytics-id-here') return;

    // Initialize Google Analytics if not already loaded
    if (!window.dataLayer) {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head.appendChild(script);

      window.dataLayer = [];
      window.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
      };
      
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        page_path: window.location.pathname,
      });
    }

    // Track page views on route changes
    const url = pathname + (searchParams.toString() ? `?${searchParams}` : '');
    if (window.gtag) {
      window.gtag('event', 'page_view', { 
        page_path: url,
        page_title: document.title,
        page_location: window.location.href
      });
    }
  }, [pathname, searchParams]);
}

export function trackEvent(eventName: string, eventParams: EventParams = {}): void {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

// Common event tracking helpers
export function trackPurchase(transactionId: string, value: number, currency: string = 'USD', items: any[] = []): void {
  trackEvent('purchase', {
    transaction_id: transactionId,
    value,
    currency,
    items
  });
}

export function trackCheckoutStart(value: number, currency: string = 'USD', items: any[] = []): void {
  trackEvent('begin_checkout', {
    value,
    currency,
    items
  });
}

export function trackAddToCart(itemName: string, value: number, currency: string = 'USD'): void {
  trackEvent('add_to_cart', {
    item_name: itemName,
    value,
    currency
  });
}